import React, { useState, useEffect } from 'react'
import './ReviewForm.css'
import { GOOGLE_CLIENT_ID } from '../config/googleAuth'

function ReviewForm({ isOpen, onClose, facilities }) {
    const [googleUser, setGoogleUser] = useState(null);
    const [selectedFacility, setSelectedFacility] = useState('');
    const [scanType, setScanType] = useState('MRI');
    const [daysWaited, setDaysWaited] = useState('');
    const [facilitySearch, setFacilitySearch] = useState('');
    const [showFacilityDropdown, setShowFacilityDropdown] = useState(false);
    const [showGoogleFallbackButton, setShowGoogleFallbackButton] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });

    // Load Google Sign-In script and render button
    useEffect(() => {
        const renderGoogleButton = () => {
            if (window.google && window.google.accounts && !googleUser) {
                const container = document.getElementById('google-sign-in-container');
                if (container) {
                    container.innerHTML = '';
                    window.google.accounts.id.renderButton(container, {
                        theme: 'outline',
                        size: 'large',
                        width: '100%',
                        text: 'signin_with'
                    });
                }
            }
        };

        if (!window.google) {
            const script = document.createElement('script');
            script.src = 'https://accounts.google.com/gsi/client';
            script.async = true;
            script.defer = true;
            script.onload = () => {
                initializeGoogleSignIn();
                // Small delay to ensure initialization is complete
                setTimeout(renderGoogleButton, 200);
            };
            document.head.appendChild(script);
        } else {
            initializeGoogleSignIn();
            setTimeout(renderGoogleButton, 100);
        }
    }, [googleUser, isOpen]);

    const initializeGoogleSignIn = () => {
        if (window.google && window.google.accounts) {
            if (GOOGLE_CLIENT_ID === 'YOUR_GOOGLE_CLIENT_ID') {
                console.warn('Google Client ID not configured. Please set REACT_APP_GOOGLE_CLIENT_ID or update config/googleAuth.js');
                setMessage({ type: 'error', text: 'Google Sign-In not configured. Please contact administrator.' });
                return;
            }
            
            window.google.accounts.id.initialize({
                client_id: GOOGLE_CLIENT_ID,
                callback: handleCredentialResponse
            });
        }
    };

    const handleCredentialResponse = (response) => {
        // Decode the JWT token (basic decode, server will verify)
        try {
            const payload = JSON.parse(atob(response.credential.split('.')[1]));
            setGoogleUser({
                email: payload.email,
                name: payload.name,
                token: response.credential
            });
            setMessage({ type: 'success', text: 'Signed in successfully!' });
        } catch (error) {
            setMessage({ type: 'error', text: 'Failed to sign in. Please try again.' });
        }
    };

    const handleSignIn = () => {
        // Button is already rendered, this function is no longer needed
        // but kept for backwards compatibility
        setShowGoogleFallbackButton(true);
    };

    const handleSignOut = () => {
        setGoogleUser(null);
        setShowGoogleFallbackButton(false);
        setMessage({ type: '', text: '' });
    };

    const handleFacilityInputChange = (e) => {
        const value = e.target.value;
        setFacilitySearch(value);
        setShowFacilityDropdown(true);

        // If the user types after selecting, force them to re-select from the dropdown
        if (selectedFacility && value !== selectedFacility) {
            setSelectedFacility('');
        }
    };

    const handleFacilitySelect = (facility) => {
        setSelectedFacility(facility.hospital);
        setFacilitySearch(facility.hospital);
        setShowFacilityDropdown(false);
    };

    const handleFacilityBlur = () => {
        // Give time for click selection to register before closing
        setTimeout(() => {
            setShowFacilityDropdown(false);

            // If user typed something but didn't select an option, clear it
            if (facilitySearch && !selectedFacility) {
                setFacilitySearch('');
                setMessage({ type: 'error', text: 'Please select an imaging centre from the dropdown list.' });
            }
        }, 150);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!googleUser) {
            setMessage({ type: 'error', text: 'Please sign in with Google first' });
            return;
        }

        if (!selectedFacility) {
            setMessage({ type: 'error', text: 'Please select an imaging centre' });
            return;
        }

        const daysWaitedNum = parseFloat(daysWaited);
        if (!daysWaited || isNaN(daysWaitedNum) || daysWaitedNum <= 0 || daysWaitedNum > 999.99) {
            setMessage({ type: 'error', text: 'Please enter a valid number of days (0 to 999.99)' });
            return;
        }

        setIsSubmitting(true);
        setMessage({ type: '', text: '' });

        try {
            // Adjust API path based on environment
            const apiPath = process.env.PUBLIC_URL ? 
                `${process.env.PUBLIC_URL}/api/reviews.php` : 
                '/api/reviews.php';
            
            const response = await fetch(`${apiPath}?action=submit`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    token: googleUser.token,
                    facility_name: selectedFacility,
                    scan_type: scanType,
                    days_waited: parseFloat(daysWaited)
                })
            });

            if (!response.ok) {
                const text = await response.text();
                throw new Error(`API error (${response.status}): ${text.slice(0, 200)}`);
            }

            const contentType = response.headers.get('content-type') || '';
            if (!contentType.includes('application/json')) {
                const text = await response.text();
                throw new Error(`API returned non-JSON: ${text.slice(0, 200)}`);
            }

            const result = await response.json();

            if (result.success) {
                setMessage({ type: 'success', text: 'Review submitted successfully! Thank you for your contribution.' });
                // Reset form
                setSelectedFacility('');
                setDaysWaited('');
                setScanType('MRI');
                setFacilitySearch('');
                // Close after 2 seconds
                setTimeout(() => {
                    onClose();
                    // Reload page to update wait times
                    window.location.reload();
                }, 2000);
            } else {
                setMessage({ type: 'error', text: result.error || 'Failed to submit review' });
            }
        } catch (error) {
            setMessage({ type: 'error', text: error.message || 'Network error. Please try again.' });
        } finally {
            setIsSubmitting(false);
        }
    };

    // Filter facilities based on search
    const filteredFacilities = facilities.filter(facility =>
        facility.hospital.toLowerCase().includes(facilitySearch.toLowerCase()) ||
        facility.address.toLowerCase().includes(facilitySearch.toLowerCase())
    ).slice(0, 10); // Limit to 10 results

    if (!isOpen) return null;

    return (
        <div className="review-form-overlay" onClick={onClose}>
            <div className="review-form-container" onClick={(e) => e.stopPropagation()}>
                <button className="review-form-close" onClick={onClose}>×</button>
                
                <h2>Submit a Review</h2>
                <p className="review-form-subtitle">Help others by sharing your wait time experience</p>

                {/* Google Sign-In Section */}
                <div className="review-form-auth">
                    {!googleUser ? (
                        <div>
                            <p>Please sign in with Google to submit a review:</p>
                            <div style={{ marginTop: '10px' }}>
                                <div id="google-sign-in-container"></div>
                            </div>
                        </div>
                    ) : (
                        <div className="review-form-signed-in">
                            <p>Signed in as: <strong>{googleUser.email}</strong></p>
                            <button type="button" className="sign-out-btn" onClick={handleSignOut}>
                                Sign Out
                            </button>
                        </div>
                    )}
                </div>

                {/* Review Form */}
                <form onSubmit={handleSubmit} className="review-form">
                    <div className="review-form-group review-form-group--facility">
                        <label htmlFor="facility-search">Search for Imaging centre:</label>
                        <input
                            id="facility-search"
                            type="text"
                            placeholder="Type imaging centre name or address..."
                            value={facilitySearch}
                            onChange={handleFacilityInputChange}
                            onFocus={() => setShowFacilityDropdown(true)}
                            onBlur={handleFacilityBlur}
                            className="review-form-input"
                            autoComplete="off"
                        />
                        {showFacilityDropdown && facilitySearch && !selectedFacility && filteredFacilities.length > 0 && (
                            <div className="facility-dropdown">
                                {filteredFacilities.map((facility, index) => (
                                    <div
                                        key={index}
                                        className="facility-option"
                                        onMouseDown={(e) => e.preventDefault()}
                                        onClick={() => handleFacilitySelect(facility)}
                                    >
                                        <div className="facility-name">{facility.hospital}</div>
                                        <div className="facility-address">{facility.address}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                        {selectedFacility && (
                            <div className="selected-facility">
                                Selected: <strong>{selectedFacility}</strong>
                            </div>
                        )}
                    </div>

                    <div className="review-form-group">
                        <label htmlFor="scan-type">Scan Type:</label>
                        <select
                            id="scan-type"
                            value={scanType}
                            onChange={(e) => setScanType(e.target.value)}
                            className="review-form-select"
                        >
                            <option value="MRI">MRI</option>
                            <option value="CT">CT</option>
                        </select>
                    </div>

                    <div className="review-form-group">
                        <label htmlFor="days-waited">Days Waited:</label>
                        <input
                            id="days-waited"
                            type="number"
                            min="0"
                            max="999.99"
                            step="0.01"
                            placeholder="e.g., 45.5 or 30"
                            value={daysWaited}
                            onChange={(e) => setDaysWaited(e.target.value)}
                            className="review-form-input"
                            required
                        />
                    </div>

                    {message.text && (
                        <div className={`review-form-message ${message.type}`}>
                            {message.text}
                        </div>
                    )}

                    <button
                        type="submit"
                        className="review-form-submit"
                        disabled={isSubmitting || !googleUser}
                    >
                        {isSubmitting ? 'Submitting...' : 'Submit Review'}
                    </button>
                </form>
            </div>
        </div>
    );
}

export default ReviewForm;
